import React, { Component } from 'react'
import { Query } from '@apollo/client/react/components'
import { withApollo } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import moment from 'moment'
import {
	Alert,
	Tag,
	Breadcrumb,
	Descriptions,
	Tabs,
	Collapse,
	Switch,
	Divider,
	Button,
	Modal,
	Typography,
	DatePicker,
	Input,
	Space,
	Timeline,
	Dropdown,
	Menu,
	message
} from 'antd'
import { Link, Redirect } from 'react-router-dom'
import { injectIntl, FormattedMessage, FormattedDate } from 'react-intl'
import {
	faTrash,
	faEdit,
	faCopy,
	faList,
	faCommentDots,
	faQuoteLeft,
	faQuoteRight,
	faThumbsUp,
	faThumbsDown,
	faUpload,
	faClipboard,
	faComments,
	faLink
} from '@fortawesome/free-solid-svg-icons'
import { faSlack } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { LoadingOutlined } from '@ant-design/icons'

import Comments from '../../components/CommentsPage'
import Loading from '../../components/LoadingPage'
import config from '../../config'
import Title from '../../components/Title'
import S from '../../components/Styles'
import { setPageTitle, displayGraphQLErrors } from '../../components/Utils'
import { getDateFromObjectIdString } from '../../utils/date'
import { copyToClipboard } from '../../utils/clipboard'
import { SamplesTableQuery, SampleDetailsQuery } from './Queries'
import EditSample from './EditSample'
import OrderedTrackList from './OrderedTrackList'
import Review from './Reviews'
import * as Utils from '../../components/Utils'
import { SelectTeamModal } from '../teams/SelectTeam'
import * as Auth from '../../auth'
import { compact, debounce, sample } from 'lodash'

export const getSamplePoolTagsString = (sample) => {
	const poolTags = sample?.poolTags || {}
	return [
		...(poolTags.genres || []),
		...(poolTags.subgenres || []),
		...(poolTags.tags || []),
		...(poolTags.intensities || []),
		...(poolTags.langs || [])
	].join(', ')
}

class ISampleDetails extends Component {
	state = {
		editModal: false,
		duplicateModal: false,
		selectTeamModal: false,
		allowReview: true,

		// the ongoing description not yet commited in the backend
		description: null,
		imageUploading: false
	}

	async updateExpirationDate(expirationDate) {
		const { sample, client } = this.props
		try {
			const r = await client.mutate({
				variables: {
					id: sample._id,
					expirationDate: expirationDate
				},
				mutation: gql`
					mutation updateSample($id: String!, $expirationDate: Date) {
						sampleUpdateById(
							_id: $id
							expirationDate: $expirationDate
						) {
							recordId
						}
					}
				`,
				refetchQueries: [
					{
						query: SamplesTableQuery
					},
					{
						query: SampleDetailsQuery,
						variables: { id: sample._id }
					}
				],
				awaitRefetchQueries: true
			})

			Utils.parseMutationResponse(r)
		} catch (e) {
			Utils.displayError(e)
		}
	}

	debouncedUpdateDescription = debounce(
		(description) => this.updateDescription(description),
		1000
	)

	async updateDescription(description) {
		const { sample, client } = this.props
		try {
			const r = await client.mutate({
				variables: {
					id: sample._id,
					description: description ?? ''
				},
				mutation: gql`
					mutation updateSample($id: String!, $description: String!) {
						sampleUpdateById(_id: $id, description: $description) {
							recordId
						}
					}
				`,
				refetchQueries: [
					{
						query: SampleDetailsQuery,
						variables: { id: sample._id }
					}
				],
				awaitRefetchQueries: true
			})

			Utils.parseMutationResponse(r)
			this.setState({ description: null })
		} catch (e) {
			Utils.displayError(e)
		}
	}

	onSelectFile = async (e) => {
		const file = e.target.files[0]
		if (file) {
			const { width, height } =
				await this.getUploadedImageDimensions(file)

			if (width < 900 || height < 600) {
				Utils.displaySpecificError('samples.visual.errors.tooSmall')
			} else {
				await this.uploadImage(file)
			}
		}
	}

	getUploadedImageDimensions = async (file) => {
		return new Promise((resolve, reject) => {
			const img = new Image()
			img.onload = () => {
				const { naturalWidth: width, naturalHeight: height } = img
				URL.revokeObjectURL(img.src)
				resolve({ width, height })
			}
			img.onerror = reject
			img.src = URL.createObjectURL(file)
		})
	}

	uploadImage = async (file) => {
		const { client, sample } = this.props

		this.setState({ imageUploading: true })

		try {
			//
			const result = await client.mutate({
				variables: { file, id: sample._id },
				mutation: gql`
					mutation uploadSampleImage($file: Upload!, $id: String) {
						sampleUploadImage(file: $file, id: $id) {
							success
							message
							url
						}
					}
				`,
				refetchQueries: [
					{
						query: SampleDetailsQuery,
						variables: { id: sample._id }
					}
				],
				awaitRefetchQueries: true
			})
			Utils.parseMutationResponse(result)
			this.setState({
				imageUploading: false
			})
		} catch (e) {
			this.setState({ imageUploading: false })
			Utils.displayError(e)
		}
	}

	removeImage = async () => {
		const { sample, client } = this.props
		try {
			const r = await client.mutate({
				variables: {
					id: sample._id
				},
				mutation: gql`
					mutation deleteImage($id: String!) {
						sampleDeleteImage(id: $id) {
							recordId
						}
					}
				`,
				refetchQueries: [
					{
						query: SampleDetailsQuery,
						variables: { id: sample._id }
					}
				],
				awaitRefetchQueries: true
			})

			Utils.parseMutationResponse(r)
		} catch (e) {
			Utils.displayError(e)
		}
	}

	sampleLink = () => {
		const { sample } = this.props
		const kind = this.state.allowReview ? 'review' : 'listen'
		return {
			kind,
			url: `${window.location.protocol}//${window.location.host}/${kind}/${sample._id}`
		}
	}

	copy = async (content) => {
		try {
			await copyToClipboard(content)
			message.success(
				this.props.intl.formatMessage({
					id: 'messages.copiedToClipboard'
				})
			)
		} catch (e) {
			Utils.displayError(e)
		}
	}

	copyLink = () => this.copy({ text: this.sampleLink().url })

	copySlackMessage = () => {
		const { sample } = this.props
		const { kind, url } = this.sampleLink()
		const label = kind === 'review' ? 'REVIEW' : 'LISTEN'
		const prefix = `${sample.name} - `

		// Build the rich version through the DOM so the sample name is escaped.
		const anchor = document.createElement('a')
		anchor.href = url
		anchor.textContent = label
		const html = document.createElement('span')
		html.append(prefix, anchor)

		return this.copy({
			text: `${prefix}${label} ${url}`,
			html: html.innerHTML
		})
	}

	render() {
		const { sample, intl, client } = this.props
		setPageTitle(intl, null, sample.name)

		let actions = []
		if (Auth.hasAccess('sample:edit', sample.teamId)) {
			actions.push({
				title: intl.formatMessage({ id: 'actions.edit' }),
				key: 'editSample',
				icon: faEdit,
				dataTest: 'edit-sample',
				action: () => this.setState({ editModal: true })
			})
		}

		if (Auth.hasAccess('sample:add', sample.teamId)) {
			actions.push({
				title: intl.formatMessage({
					id: 'samples.actions.duplicateSample'
				}),
				key: 'duplicateSample',
				icon: faCopy,
				dataTest: 'duplicate-sample',
				action: () => this.setState({ duplicateModal: true })
			})
		}

		if (
			Auth.hasPermission('sample:edit:setTeam') &&
			Auth.hasAccess('sample:edit', sample.teamId)
		) {
			actions.push({
				title: intl.formatMessage({
					id: 'samples.actions.assignSampleToTeam'
				}),
				key: 'reassignSample',
				icon: faEdit,
				dataTest: 'reassign-sample',
				action: () => this.setState({ selectTeamModal: true })
			})
		}

		if (Auth.hasAccess('sample:delete', sample.teamId)) {
			actions.push({
				title: intl.formatMessage({ id: 'actions.delete' }),
				key: 'deleteSample',
				icon: faTrash,
				confirm: intl.formatMessage(
					{ id: 'samples.actions.delete.confirm' },
					{ name: sample.name }
				),
				mutation: {
					variables: { id: sample._id },
					mutation: gql`
						mutation removeSample($id: String!) {
							sampleRemoveById(_id: $id) {
								recordId
							}
						}
					`,
					refetchQueries: [
						{
							query: SamplesTableQuery
						},
						{
							query: SampleDetailsQuery,
							variables: { id: sample._id }
						}
					],
					awaitRefetchQueries: true
				}
			})
		}

		const { url: sampleUrl } = this.sampleLink()
		const isExpired =
			sample.expirationDate &&
			moment(sample.expirationDate).isBefore(moment())

		// generate the sample path breadcrumb without the playlist itself
		const samplePath = [...(sample.playlist?.path || [])]
		samplePath.pop()

		return (
			<div>
				<Title
					title={sample.name}
					actions={actions}
					backUrl="/samples"
					backTitle="samples"
					dataTest="main-sample-menu"
				>
					<div
						style={S.itemDetails.teamPath}
						data-test="sample-selected-team"
					>
						{sample.team && (
							<Link to={`/teams/${sample.team._id}`}>
								<Tag color="blue">{sample.team.path}</Tag>
							</Link>
						)}
					</div>
				</Title>

				<Tabs>
					<Tabs.TabPane
						tab={
							<span>
								<FontAwesomeIcon icon={faList} />{' '}
								<FormattedMessage id="sample" />
							</span>
						}
						key="1"
					>
						<Descriptions column={1} bordered>
							<Descriptions.Item
								label={intl.formatMessage({ id: 'playlist' })}
							>
								<Breadcrumb
									separator=">"
									style={{ marginBottom: 10 }}
								>
									{samplePath.map((t) => (
										<Breadcrumb.Item key={t}>
											{t}
										</Breadcrumb.Item>
									))}
									{sample.playlist ?
										<Breadcrumb.Item
											style={{ cursor: 'pointer' }}
											onClick={() =>
												this.setState({
													editModal: true
												})
											}
										>
											{sample.playlist.n ||
												intl.formatDate(
													sample.playlist.created,
													{
														day: 'numeric',
														month: 'long',
														year: 'numeric'
													}
												)}
										</Breadcrumb.Item>
									:	<p style={S.emptyText}>
											<FormattedMessage id="nonef" />
										</p>
									}
								</Breadcrumb>
							</Descriptions.Item>
							<Descriptions.Item
								label={intl.formatMessage({
									id: 'samples.visual.label'
								})}
							>
								<Space>
									<Button style={{ width: 100, height: 100 }}>
										<FontAwesomeIcon icon={faUpload} />
										<input
											style={S.playlistDetails.fileInput}
											type="file"
											accept="image/*"
											ref={(e) => (this.fileInput = e)}
											onChange={this.onSelectFile}
										/>
										{this.state.imageUploading && (
											<LoadingOutlined
												style={{
													position: 'absolute',
													fontSize: 30,
													top: 'calc(50% - 15px)',
													left: 0,
													right: 0
												}}
											/>
										)}
									</Button>

									{sample?.image && (
										<>
											<a
												target="_blank"
												href={Utils.sampleImageUrl(
													sample
												)}
											>
												<img
													style={{
														width: '100%',
														maxHeight: '100px'
													}}
													src={Utils.sampleImageUrl(
														sample
													)}
												></img>
											</a>

											<button onClick={this.removeImage}>
												<FontAwesomeIcon
													icon={faTrash}
												/>
											</button>
										</>
									)}
								</Space>
							</Descriptions.Item>
							<Descriptions.Item
								label={intl.formatMessage({
									id: 'samples.description.label'
								})}
							>
								<Input.TextArea
									rows={4}
									placeholder={intl.formatMessage({
										id: 'samples.description.placeholder'
									})}
									value={
										this.state.description ??
										sample.description ??
										''
									}
									onChange={(v) => {
										const value = v?.target?.value
										this.setState({ description: value })
										this.debouncedUpdateDescription(value)
									}}
								/>
							</Descriptions.Item>
							<Descriptions.Item
								label={intl.formatMessage({
									id: 'samples.poolTags.label'
								})}
							>
								<Typography.Text>
									{getSamplePoolTagsString(sample)}
								</Typography.Text>
							</Descriptions.Item>
							<Descriptions.Item
								label={intl.formatMessage({
									id: 'samples.expirationDate'
								})}
							>
								<Space size="large" wrap="wrap">
									<DatePicker
										allowClear={true}
										style={{ width: 300 }}
										format="LL"
										placeholder={intl.formatMessage({
											id: 'samples.expirationDate'
										})}
										value={
											sample.expirationDate ?
												moment(sample.expirationDate)
											:	null
										}
										onChange={async (v) => {
											await this.updateExpirationDate(
												v ? v.toDate() : null
											)
										}}
									/>

									{isExpired && (
										<Typography.Text type="warning">
											<FormattedMessage id="samples.isExpired"></FormattedMessage>
										</Typography.Text>
									)}
								</Space>
							</Descriptions.Item>

							{!isExpired && (
								<Descriptions.Item
									label={intl.formatMessage({
										id: 'samples.url'
									})}
								>
									<div style={{ marginBottom: 10 }}>
										<Switch
											checked={this.state.allowReview}
											onChange={(v) =>
												this.setState({
													allowReview: v
												})
											}
										/>
										&nbsp;&nbsp;
										<FormattedMessage id="samples.url.allowFeedback" />
									</div>
									<div className="sample-preview">
										<a
											href={sampleUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											{sampleUrl}
										</a>
										&nbsp;&nbsp;
										<Dropdown.Button
											onClick={this.copyLink}
											trigger={['click']}
											style={{ marginTop: 10 }}
											overlay={
												<Menu
													onClick={({ key }) => {
														if (
															key ===
															'copySlackMessage'
														) {
															this.copySlackMessage()
														}
													}}
												>
													<Menu.Item
														key="copySlackMessage"
														data-test="copy-slack-message"
													>
														<FontAwesomeIcon
															icon={faSlack}
															style={{
																marginRight: 5
															}}
															fixedWidth
														/>
														<FormattedMessage id="samples.url.copySlackMessage" />
													</Menu.Item>
												</Menu>
											}
										>
											<FontAwesomeIcon
												icon={faLink}
												style={{ marginRight: 5 }}
												fixedWidth
											/>
											<FormattedMessage id="samples.url.copyUrl" />
										</Dropdown.Button>
									</div>
								</Descriptions.Item>
							)}
						</Descriptions>

						{this.state.editModal && (
							<EditSample
								sample={sample}
								onClose={() =>
									this.setState({ editModal: false })
								}
							/>
						)}
						{this.state.duplicateModal && (
							<EditSample
								duplicate
								sample={sample}
								onClose={() =>
									this.setState({ duplicateModal: false })
								}
							/>
						)}
						{this.state.selectTeamModal && (
							<SelectTeamModal
								title={this.props.intl.formatMessage({
									id: 'samples.actions.assignSampleToTeam'
								})}
								onClose={() =>
									this.setState({ selectTeamModal: false })
								}
								selected={sample.teamId}
								hideNoParents
								onSelect={async (t) => {
									try {
										if (t && t !== sample.teamId) {
											const r = await client.mutate({
												variables: {
													id: sample._id,
													parent: t
												},
												mutation: gql`
													mutation updateSampleTeam(
														$id: String!
														$parent: String!
													) {
														sampleUpdateById(
															_id: $id
															teamId: $parent
														) {
															recordId
														}
													}
												`,
												refetchQueries: [
													{
														query: SampleDetailsQuery,
														variables: {
															id: sample._id
														}
													}
												]
											})
											Utils.parseMutationResponse(r)
										}
									} catch (e) {
										Utils.displayError(e)
									}
								}}
							/>
						)}

						<OrderedTrackList {...sample} onChange={() => {}} />
					</Tabs.TabPane>
					<Tabs.TabPane
						tab={
							<span>
								<FontAwesomeIcon icon={faCommentDots} />{' '}
								<FormattedMessage
									id="samples.feedback"
									values={{
										count: sample.reviews?.length ?? 0
									}}
								/>
							</span>
						}
						key="2"
					>
						{sample.reviews.length ?
							<Collapse
								style={{ width: '100%' }}
								className="column-browser"
							>
								{sample.reviews.map((r) => {
									let likes = 0
									let dislikes = 0
									let comments = 0
									for (let t of r.tracks) {
										if (t.rating === 1) {
											dislikes++
										} else if (t.rating === 2) {
											likes++
										}
										if (t.comment) {
											comments++
										}
									}

									return (
										<Collapse.Panel
											key={r._id}
											header={
												<div>
													<div>
														<span
															style={{
																fontWeight: 600
															}}
														>
															{r.createdBy.name}
														</span>
														<Divider type="vertical" />
														<span
															style={{
																color: '#999'
															}}
														>
															{r.createdBy.email}
														</span>
														<Divider type="vertical" />
														<span
															style={{
																color: '#999'
															}}
														>
															<FormattedDate
																value={
																	r.createdBy
																		?.date
																}
																day="numeric"
																month="long"
																year="numeric"
															/>
														</span>
														<Divider type="vertical" />
														<span
															style={{
																color: '#999'
															}}
														>
															<FontAwesomeIcon
																icon={
																	faThumbsUp
																}
															/>
															&nbsp;{likes}
															&nbsp;&nbsp;&nbsp;&nbsp;
															<FontAwesomeIcon
																icon={
																	faThumbsDown
																}
															/>
															&nbsp;{dislikes}
															&nbsp;&nbsp;&nbsp;&nbsp;
															<FontAwesomeIcon
																icon={
																	faCommentDots
																}
															/>
															&nbsp;{comments}
														</span>
													</div>
													<div>
														{r.comment && (
															<div
																style={{
																	display:
																		'flex',
																	alignItems:
																		'center'
																}}
															>
																<FontAwesomeIcon
																	icon={
																		faQuoteLeft
																	}
																/>
																<p
																	style={{
																		margin: '0 10px'
																	}}
																>
																	{r.comment}
																</p>
																<FontAwesomeIcon
																	icon={
																		faQuoteRight
																	}
																/>
															</div>
														)}
													</div>
												</div>
											}
										>
											<Review
												review={r}
												sample={sample}
											/>
										</Collapse.Panel>
									)
								})}
							</Collapse>
						:	<div style={S.empty}>
								<FormattedMessage id="samples.noReviews" />
							</div>
						}
					</Tabs.TabPane>
					{Auth.hasPermission('sample:read:note') && (
						<Tabs.TabPane
							tab={
								<span>
									<FontAwesomeIcon icon={faClipboard} />{' '}
									<FormattedMessage
										id="spectreNotes"
										values={{ count: sample.commentCount }}
									/>
								</span>
							}
							key="4"
						>
							<Timeline mode="left" style={{ marginTop: 20 }}>
								<Timeline.Item
									color="green"
									label={new Date(
										sample.createdBy?.date
									).toLocaleString()}
								>
									Created (
									{compact([
										sample.createdBy?.name,
										sample.createdBy?.email
									]).join(' / ')}
									)
								</Timeline.Item>
								<Timeline.Item
									label={new Date(
										sample.updatedBy?.date
									).toLocaleString()}
								>
									<Typography.Text>
										Last updated (
										{compact([
											sample.updatedBy?.name,
											sample.updatedBy?.email
										]).join(' / ')}
										)
									</Typography.Text>
								</Timeline.Item>
							</Timeline>
							<Divider orientation="left">
								<FontAwesomeIcon icon={faComments} /> Comments
							</Divider>
							<Comments
								objectId={sample._id}
								writePermission="sample:edit:setNote"
								refetchQueries={['fetchSampleDetails']}
							/>
						</Tabs.TabPane>
					)}
				</Tabs>
			</div>
		)
	}
}

const SampleDetails = withApollo(injectIntl(ISampleDetails))

class SampleDetailsFetch extends Component {
	render() {
		const sample = this.props.match.params.sampleId
		return (
			<div>
				{sample && (
					<Query
						pollInterval={config.longPollInterval}
						query={SampleDetailsQuery}
						variables={{ id: sample }}
					>
						{({ loading, error, data }) => {
							if ((!data || !data.sampleById) && loading) {
								return <Loading />
							}
							if (error) {
								return (
									<Alert
										message={this.props.intl.formatMessage({
											id: 'error'
										})}
										description={displayGraphQLErrors(
											error
										)}
										type="error"
										showIcon
									/>
								)
							}

							if (!data.sampleById) {
								return <Redirect to="/samples" />
							}

							return <SampleDetails sample={data.sampleById} />
						}}
					</Query>
				)}
			</div>
		)
	}
}

export default injectIntl(SampleDetailsFetch)
